import { gql } from '@apollo/client';
import compact from 'lodash/compact';
import { useMemo } from 'react';
import {
  RouteRouteBoolExp,
  useGetLineRoutesByLabelQuery,
  useGetRouteByFiltersQuery,
} from '../../../../generated/graphql';
import { Operation } from '../../../../redux';
import { buildActiveDateGqlFilter } from '../../../../utils';
import { filterRoutesByHighestPriority } from '../../../LinesAndRoutes/Common';
import { useMapUrlStateContext } from '../../Utils/mapUrlState';
import { useMapDataLayerLoader } from '../../Utils/useMapDataLayerLoader';

const GQL_GET_LINE_ROUTES_BY_LABEL = gql`
  query GetLineRoutesByLabel(
    $lineFilters: route_line_bool_exp
    $lineRouteFilters: route_route_bool_exp
  ) {
    route_line(where: $lineFilters) {
      line_id
      line_routes(where: $lineRouteFilters) {
        ...DisplayedRoute
      }
    }
  }
`;

const GQL_GET_ROUTE_BY_FILTERS = gql`
  query GetRouteByFilters($routeFilters: route_route_bool_exp) {
    route_route(where: $routeFilters) {
      ...DisplayedRoute
    }
  }
`;

const GQL_DISPLAYED_ROUTE = gql`
  fragment DisplayedRoute on route_route {
    route_id
    label
    variant
    validity_start
    validity_end
    priority
    direction
    route_journey_patterns {
      journey_pattern_id
      scheduled_stop_point_in_journey_patterns {
        journey_pattern_id
        scheduled_stop_point_label
        scheduled_stop_point_sequence
      }
    }
  }
`;

export function useGetRoutesDisplayedInMap() {
  const {
    state: {
      displayedRoute: {
        routeLabels,
        lineLabel,
        routeId,
        showSelectedDaySituation,
        routePriorities,
      },
      filters: { observationDate },
    },
  } = useMapUrlStateContext();

  const routeFilters: RouteRouteBoolExp = {
    ...buildActiveDateGqlFilter(observationDate),
    priority: { _in: routePriorities },
  };

  const skipRoutesByRouteInfoResult = !routeLabels.length && !routeId;
  // Get routes by ROUTE LABEL OR ID
  const routesByRouteInfoResult = useGetRouteByFiltersQuery({
    variables: {
      routeFilters: {
        _and: [
          {
            _or: compact([
              { label: { _in: routeLabels } },
              routeId ? { route_id: { _eq: routeId } } : null,
            ]),
          },
          routeFilters,
        ],
      },
    },
    skip: skipRoutesByRouteInfoResult,
  });

  const skipRoutesByLineLabelResult = !lineLabel;
  const routesByLineLabelResult = useGetLineRoutesByLabelQuery({
    variables: {
      lineFilters: { label: { _eq: lineLabel } },
      lineRouteFilters: routeFilters,
    },
    skip: skipRoutesByLineLabelResult,
  });

  const rawRoutesByRoute = routesByRouteInfoResult.data?.route_route;
  const rawLines = routesByLineLabelResult?.data?.route_line;

  const routes = useMemo(() => {
    const lineRoutes = rawLines?.flatMap((line) => line.line_routes);

    return [...(rawRoutesByRoute ?? []), ...(lineRoutes ?? [])];
  }, [rawRoutesByRoute, rawLines]);

  // If we choose not to show the situation on selected day,
  // do not filter routes
  const displayedRoutes = useMemo(
    () =>
      showSelectedDaySituation ? filterRoutesByHighestPriority(routes) : routes,
    [routes, showSelectedDaySituation],
  );

  const displayedRouteIds = useMemo(
    () => displayedRoutes.map((route) => route.route_id),
    [displayedRoutes],
  );

  const initialLoadDone =
    !!(
      routesByRouteInfoResult.previousData ??
      routesByRouteInfoResult.data ??
      routesByLineLabelResult.previousData ??
      routesByLineLabelResult.data
    ) ||
    (skipRoutesByRouteInfoResult && skipRoutesByLineLabelResult);
  const isLoading =
    routesByRouteInfoResult.loading || routesByLineLabelResult.loading;

  useMapDataLayerLoader(Operation.FetchRoutes, initialLoadDone, isLoading);

  return { displayedRoutes, displayedRouteIds, loading: isLoading };
}
